import React, { useState, useEffect } from 'react';
import LocationDetector from './LocationDetector';
import './WorkerInterface.css';
import { toast, Toaster } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

const WorkerInterface = ({ data }) => {
  const { fields: propFields } = data || { fields: [] };
  const [localFields, setLocalFields] = useState([]);
  const [detectedField, setDetectedField] = useState(null);
  const [workSession, setWorkSession] = useState({
    fieldId: '',
    activityType: '',
    startTime: null,
    endTime: null,
    notes: ''
  });
  const [sessionActive, setSessionActive] = useState(false);
  const [availableEquipment, setAvailableEquipment] = useState([
    { id: 'tractor1', name: 'John Deere Tractor', type: 'tractor' },
    { id: 'sprayer1', name: 'Sprayer 2000', type: 'sprayer' },
    { id: 'plow1', name: 'Field Plow XL', type: 'plow' },
    { id: 'harvester1', name: 'Harvester Pro', type: 'harvester' }
  ]);
  const [availableMaterials, setAvailableMaterials] = useState([
    { id: 'herb1', name: 'Roundup', type: 'herbicide', unit: 'liters' },
    { id: 'herb2', name: 'Glyphosate 360', type: 'herbicide', unit: 'liters' },
    { id: 'fert1', name: 'NPK 15-15-15', type: 'fertilizer', unit: 'kg' },
    { id: 'fert2', name: 'Urea 46%', type: 'fertilizer', unit: 'kg' },
    { id: 'seed1', name: 'Wheat Seeds', type: 'seeds', unit: 'kg' },
    { id: 'seed2', name: 'Corn Seeds', type: 'seeds', unit: 'kg' },
  ]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [equipmentFuelLevels, setEquipmentFuelLevels] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [materialQuantities, setMaterialQuantities] = useState({});
  const [materialApplications, setMaterialApplications] = useState([]);
  const [workSessions, setWorkSessions] = useState([]);
  const [activeTab, setActiveTab] = useState('newSession');
  const [assignedTasks, setAssignedTasks] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const [activityType, setActivityType] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  
  // Current user (in a real app, this would come from authentication)
  const currentUser = { id: 'w2', name: 'Jane Smith' };

  // Activity types that workers can select
  const activityTypes = [
    { id: 'plowing', name: 'Plowing' },
    { id: 'planting', name: 'Planting' },
    { id: 'fertilizing', name: 'Fertilizing' },
    { id: 'spraying', name: 'Spraying' },
    { id: 'irrigating', name: 'Irrigating' },
    { id: 'harvesting', name: 'Harvesting' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'other', name: 'Other' }
  ];

  // Task types for assigned tasks
  const taskTypes = [
    { id: 'plowing', name: 'Plowing' },
    { id: 'planting', name: 'Planting' },
    { id: 'fertilizing', name: 'Fertilizing' },
    { id: 'spraying', name: 'Spraying' },
    { id: 'irrigating', name: 'Irrigating' },
    { id: 'harvesting', name: 'Harvesting' },
    { id: 'maintenance', name: 'Maintenance' },
    { id: 'inspection', name: 'Inspection' },
    { id: 'other', name: 'Other' }
  ];

  // Load saved work sessions from localStorage
  useEffect(() => {
    const savedSessions = localStorage.getItem('agrimanage-work-sessions');
    if (savedSessions) {
      setWorkSessions(JSON.parse(savedSessions));
    }
    
    // Load assigned tasks from workplans
    const savedWorkPlans = localStorage.getItem('agrimanage-workplans');
    if (savedWorkPlans) {
      const workPlans = JSON.parse(savedWorkPlans);
      // Filter for tasks assigned to current user
      const assignedToUser = workPlans.filter(plan => 
        plan.assignedTo === currentUser.id && 
        (plan.status === 'assigned' || plan.status === 'inProgress')
      );
      
      setAssignedTasks(assignedToUser);
    }
    
    // Load inventory data if available
    const savedEquipment = localStorage.getItem('agrimanage-equipments');
    const savedMaterials = localStorage.getItem('agrimanage-materials');
    
    if (savedEquipment) {
      setAvailableEquipment(JSON.parse(savedEquipment));
    }
    
    if (savedMaterials) {
      setAvailableMaterials(JSON.parse(savedMaterials));
    }
    
    // Listen for cloud sync completion - RELOAD WORKER DATA
    const handleCloudSyncComplete = (event) => {
      console.log('📱 Cloud sync completed - RELOADING WorkerInterface data');
      setTimeout(() => {
        // Reload work sessions (worker's own data)
        const savedSessions = localStorage.getItem('agrimanage-work-sessions');
        if (savedSessions) {
          const parsedSessions = JSON.parse(savedSessions);
          const actualSessions = parsedSessions.data || parsedSessions; // Handle wrapped format
          setWorkSessions(actualSessions);
        }
        
        // Reload assigned tasks (from manager)
        const savedWorkPlans = localStorage.getItem('agrimanage-workplans');
        if (savedWorkPlans) {
          const parsedPlans = JSON.parse(savedWorkPlans);
          const actualPlans = parsedPlans.data || parsedPlans; // Handle wrapped format
          const assignedToUser = actualPlans.filter(plan => 
            plan.assignedTo === currentUser.id && 
            (plan.status === 'assigned' || plan.status === 'inProgress')
          );
          setAssignedTasks(assignedToUser);
        }
        
        // Reload equipment/materials (from manager/inventory)
        const savedEquipment = localStorage.getItem('agrimanage-equipments');
        if (savedEquipment) {
          const parsedEquipment = JSON.parse(savedEquipment);
          const actualEquipment = parsedEquipment.data || parsedEquipment;
          setAvailableEquipment(actualEquipment);
        }
        
        const savedMaterials = localStorage.getItem('agrimanage-materials');
        if (savedMaterials) {
          const parsedMaterials = JSON.parse(savedMaterials);
          const actualMaterials = parsedMaterials.data || parsedMaterials;
          setAvailableMaterials(actualMaterials);
        }
      }, 500); // Small delay to ensure data is written
    };
    
    window.addEventListener('cloudSyncComplete', handleCloudSyncComplete);
    window.addEventListener('workPlansDataUpdated', handleCloudSyncComplete);
    
    return () => {
      window.removeEventListener('cloudSyncComplete', handleCloudSyncComplete);
      window.removeEventListener('workPlansDataUpdated', handleCloudSyncComplete);
    };
  }, [currentUser.id]);

  // Save sessions when they change
  useEffect(() => {
    if (workSessions.length > 0) {
      localStorage.setItem('agrimanage-work-sessions', JSON.stringify(workSessions));
    }
  }, [workSessions]);

  // Keep selectedField and activityType in sync with workSession
  useEffect(() => {
    setSelectedField(workSession.fieldId);
    setActivityType(workSession.activityType);
    setSessionNotes(workSession.notes);
  }, [workSession.fieldId, workSession.activityType, workSession.notes]);

  // Handle when a field is detected by LocationDetector
  const handleFieldDetected = (field) => {
    setDetectedField(field);
    
    // If no field is selected yet, auto-select the detected field
    if (!workSession.fieldId && !sessionActive) {
      setWorkSession(prev => ({
        ...prev,
        fieldId: field.id
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setWorkSession(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Update related state variables
    if (name === 'fieldId') {
      setSelectedField(value);
    } else if (name === 'activityType') {
      setActivityType(value);
    } else if (name === 'notes') {
      setSessionNotes(value);
    }
  };

  // Toggle equipment selection
  const handleEquipmentToggle = (equipmentId) => {
    setSelectedEquipment(prev => {
      if (prev.includes(equipmentId)) {
        // Remove from selected equipment
        const newSelected = prev.filter(id => id !== equipmentId);
        
        // Remove fuel level data
        setEquipmentFuelLevels(prevLevels => {
          const newLevels = {...prevLevels};
          delete newLevels[equipmentId];
          return newLevels;
        });
        
        return newSelected;
      } else {
        return [...prev, equipmentId];
      }
    });
  };

  // Handle fuel level input interaction
  const handleFuelInputClick = (e) => {
    e.stopPropagation(); // Prevent the click from bubbling up to parent
  };

  // Update equipment fuel level
  const handleFuelLevelChange = (equipmentId, type, value) => {
    setEquipmentFuelLevels(prev => ({
      ...prev,
      [equipmentId]: {
        ...prev[equipmentId],
        [type]: value
      }
    }));
  };

  // Handle material selection toggle
  const handleMaterialToggle = (materialId) => {
    if (selectedMaterials.includes(materialId)) {
      // Remove the material
      setSelectedMaterials(selectedMaterials.filter(id => id !== materialId));
      
      // Remove any applications for this material
      setMaterialApplications(materialApplications.filter(app => app.materialId !== materialId));
      
      // Remove from quantities object
      const newQuantities = { ...materialQuantities };
      delete newQuantities[materialId];
      setMaterialQuantities(newQuantities);
    } else {
      // Add the material
      setSelectedMaterials([...selectedMaterials, materialId]);
      
      // Initialize empty quantity for this material
      setMaterialQuantities(prev => ({
        ...prev,
        [materialId]: ''
      }));
      
      // Note: We don't automatically create an application anymore
      // Applications will only be created when the user clicks "Add Application"
    }
  };
  
  // Update material quantity
  const handleMaterialQuantityChange = (materialId, quantity) => {
    setMaterialQuantities(prev => ({
      ...prev,
      [materialId]: quantity
    }));
  };
  
  // Handle adding an application
  const handleAddApplication = (materialId) => {
    if (!sessionActive) {
      toast.error('Please start a session first');
      return;
    }
    
    // Verify material exists and has a quantity
    const material = availableMaterials.find(m => m.id === materialId);
    if (!material) {
      toast.error('Material not found');
      return;
    }
    
    const quantity = materialQuantities[materialId];
    if (!quantity || isNaN(parseFloat(quantity)) || parseFloat(quantity) <= 0) {
      toast.error(`Please enter a valid quantity for ${material.name}`);
      return;
    }
    
    // Create a new application
    const newApp = {
      id: uuidv4(),
      materialId,
      materialName: material.name,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      quantity: parseFloat(quantity),
      unit: material.unit,
      notes: ''
    };
    
    // Add the new application to the state
    setMaterialApplications(prev => [...prev, newApp]);
    
    // Clear the quantity input after adding an application to prepare for next entry
    // Using setTimeout to ensure the state update happens after the current execution
    setTimeout(() => {
      setMaterialQuantities(prev => ({
        ...prev,
        [materialId]: ''
      }));
    }, 10);
    
    toast.success(`Added application for ${material.name}`);
  };
  
  // Handle updating an application
  const handleUpdateApplication = (appId, field, value) => {
    setMaterialApplications(prev => {
      return prev.map(app => 
        app.id === appId ? { ...app, [field]: value } : app
      );
    });
  };
  
  // Handle removing an application
  const handleRemoveApplication = (appId) => {
    setMaterialApplications(prev => prev.filter(app => app.id !== appId));
  };

  // Start a work session
  const handleStartSession = () => {
    // Reset validation errors
    setValidationErrors([]);
    
    // Check if a field is selected
    if (!selectedField) {
      setValidationErrors(prev => [...prev, 'Please select a field']);
      return;
    }
    
    // Check if an activity type is selected
    if (!activityType) {
      setValidationErrors(prev => [...prev, 'Please select an activity type']);
      return;
    }
    
    // Validate fuel levels for all selected drivable equipment
    let errors = [];
    
    selectedEquipment.forEach(eqId => {
      const equipment = availableEquipment.find(e => e.id === eqId);
      if (equipment && equipment.type === 'drivable') {
        const fuelLevel = equipmentFuelLevels[eqId]?.start;
        if (fuelLevel === undefined || fuelLevel === null || fuelLevel === '') {
          errors.push(`Enter starting fuel level for ${equipment.name}`);
        }
      }
    });
    
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Create a new work session
    const newSession = {
      id: Date.now().toString(),
      fieldId: selectedField,
      activityType,
      taskId: selectedTask ? selectedTask.id : null,
      startTime: new Date(),
      notes: sessionNotes
    };
    
    // Set active session
    setWorkSession(newSession);
    setSessionActive(true);
    
    // Show success message
    toast.success('Work session started');
  };

  // End a work session
  const handleEndSession = () => {
    // Reset validation errors
    setValidationErrors([]);
    
    // Collect validation errors
    let errors = [];
    
    // Validate ending fuel levels for all selected drivable equipment
    selectedEquipment.forEach(eqId => {
      const equipment = availableEquipment.find(e => e.id === eqId);
      if (equipment && equipment.type === 'drivable') {
        const fuelLevel = equipmentFuelLevels[eqId]?.end;
        if (fuelLevel === undefined || fuelLevel === null || fuelLevel === '') {
          errors.push(`Enter ending fuel level for ${equipment.name}`);
        }
      }
    });
    
    // Validate material quantities
    materialApplications.forEach(app => {
      if (!app.quantity || isNaN(parseFloat(app.quantity)) || parseFloat(app.quantity) <= 0) {
        const material = availableMaterials.find(m => m.id === app.materialId);
        const materialName = material ? material.name : 'Unknown material';
        errors.push(`Enter valid quantity for ${materialName}`);
      }
    });
    
    // If there are validation errors, show them and return
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    
    // Format the material usage
    const materialUsage = [];
    
    // Group applications by materialId to calculate totals
    const materialTotals = {};
    
    materialApplications.forEach(app => {
      const material = availableMaterials.find(m => m.id === app.materialId);
      if (!material) return;
      
      // Add individual application
      materialUsage.push({
        id: app.id,
        materialId: app.materialId,
        name: material.name,
        type: material.type,
        quantity: parseFloat(app.quantity),
        unit: material.unit,
        time: app.time,
        notes: app.notes || ''
      });
      
      // Update totals
      if (!materialTotals[app.materialId]) {
        materialTotals[app.materialId] = {
          materialId: app.materialId,
          name: material.name,
          type: material.type,
          quantity: 0,
          unit: material.unit
        };
      }
      
      materialTotals[app.materialId].quantity += parseFloat(app.quantity);
    });
    
    // Format the equipment usage
    const equipmentUsage = selectedEquipment.map(eqId => {
      const equipment = availableEquipment.find(e => e.id === eqId);
      return {
        equipmentId: eqId,
        name: equipment ? equipment.name : 'Unknown',
        type: equipment ? equipment.type : 'Unknown',
        fuelStart: equipment && equipment.type === 'drivable' ? parseFloat(equipmentFuelLevels[eqId].start) : null,
        fuelEnd: equipment && equipment.type === 'drivable' ? parseFloat(equipmentFuelLevels[eqId].end) : null
      };
    });
    
    // Create completed session record
    const endTime = new Date();
    const startTime = workSession.startTime ? new Date(workSession.startTime) : new Date();
    // Calculate duration in minutes
    const durationMs = endTime - startTime;
    const durationMinutes = Math.round(durationMs / 60000);
    
    const completedSession = {
      ...workSession,
      endTime,
      duration: durationMinutes,
      materials: materialUsage,
      materialTotals: Object.values(materialTotals),
      equipment: equipmentUsage,
      notes: sessionNotes
    };
    
    // Update work history
    setWorkSessions(prev => [completedSession, ...prev]);
    
    // If this was a task, mark it as completed
    if (selectedTask) {
      const updatedTasks = assignedTasks.map(task => 
        task.id === selectedTask.id ? {...task, status: 'Completed'} : task
      );
      setAssignedTasks(updatedTasks);
      localStorage.setItem('agrimanage-workplans', JSON.stringify(updatedTasks));
    }
    
    // Update material inventory quantities
    if (materialUsage.length > 0) {
      // We would typically call a service here to update inventory
      // For demo, we'll just show a message
      console.log('Material inventory would be updated with:', materialUsage);
    }
    
    // Reset the session state
    setSessionActive(false);
    setWorkSession({
      fieldId: '',
      activityType: '',
      notes: '',
      taskId: null,
      startTime: null
    });
    setSelectedEquipment([]);
    setEquipmentFuelLevels({});
    setSelectedMaterials([]);
    setMaterialQuantities({});
    setMaterialApplications([]);
    setSessionNotes('');
    
    // Show success message
    toast.success('Work session completed successfully');
  };

  // Get field name by ID
  const getFieldName = (fieldId) => {
    const field = propFields.find(f => f.id === fieldId);
    return field ? field.name : 'Unknown Field';
  };

  // Toggle select all fields
  const toggleSelectAllFields = () => {
    if (selectedFields.length === propFields.length) {
      // If all are selected, unselect all
      setSelectedFields([]);
      setWorkSession(prev => ({
        ...prev,
        fieldId: ''
      }));
    } else {
      // Otherwise, select all
      const allFieldIds = propFields.map(field => field.id);
      setSelectedFields(allFieldIds);
      // Set the first field as the active one in the form
      if (allFieldIds.length > 0) {
        setWorkSession(prev => ({
          ...prev,
          fieldId: allFieldIds[0]
        }));
      }
    }
  };

  // Toggle individual field selection
  const toggleFieldSelection = (fieldId) => {
    setSelectedFields(prev => {
      if (prev.includes(fieldId)) {
        const newSelection = prev.filter(id => id !== fieldId);
        // Update work session if the currently selected field is being unselected
        if (workSession.fieldId === fieldId) {
          setWorkSession(prev => ({
            ...prev,
            fieldId: newSelection.length > 0 ? newSelection[0] : ''
          }));
        }
        return newSelection;
      } else {
        const newSelection = [...prev, fieldId];
        // Update work session if no field is currently selected
        if (!workSession.fieldId) {
          setWorkSession(prev => ({
            ...prev,
            fieldId: fieldId
          }));
        }
        return newSelection;
      }
    });
  };

  // Filter materials based on activity type
  const getRelevantMaterials = () => {
    switch (workSession.activityType) {
      case 'planting':
        return availableMaterials.filter(m => m.type === 'seeds');
      case 'fertilizing':
        return availableMaterials.filter(m => m.type === 'fertilizer');
      case 'spraying':
        // Show all herbicides instead of filtering to allow multiple herbicide selection
        return availableMaterials.filter(m => m.type === 'herbicide');
      default:
        return availableMaterials;
    }
  };
  
  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Start assigned task
  const handleStartAssignedTask = (task) => {
    // Set the selected task
    setSelectedTask(task);
    
    // Set up the work session with task details
    setWorkSession({
      fieldId: task.fieldId,
      activityType: task.taskType,
      startTime: null,
      endTime: null,
      notes: task.description || '',
      taskId: task.id
    });
    
    // Pre-select materials and equipment if specified in the task
    if (task.materials && task.materials.length > 0) {
      const materialIds = task.materials.map(m => m.id);
      setSelectedMaterials(materialIds);
      
      // Set default quantities
      const quantities = {};
      task.materials.forEach(m => {
        quantities[m.id] = m.quantity;
      });
      setMaterialQuantities(quantities);
    }
    
    if (task.equipment && task.equipment.length > 0) {
      setSelectedEquipment(task.equipment.map(e => e.id));
    }
    
    // Switch to the new session tab
    setActiveTab('newSession');
    
    // Update task status to inProgress
    const savedWorkPlans = localStorage.getItem('agrimanage-workplans');
    if (savedWorkPlans) {
      const workPlans = JSON.parse(savedWorkPlans);
      const updatedWorkPlans = workPlans.map(plan => 
        plan.id === task.id 
          ? { ...plan, status: 'inProgress', updatedAt: new Date().toISOString() } 
          : plan
      );
      
      localStorage.setItem('agrimanage-workplans', JSON.stringify(updatedWorkPlans));
      
      // Update assigned tasks list
      setAssignedTasks(prev => 
        prev.map(t => t.id === task.id ? { ...t, status: 'inProgress' } : t)
      );
    }
  };

  return (
    <div className="worker-interface">
      <Toaster position="top-right" />
      <h2>Worker Interface</h2>
      
      <div className="worker-tabs">
        <button 
          className={`tab-button ${activeTab === 'newSession' ? 'active' : ''}`}
          onClick={() => handleTabChange('newSession')}
        >
          Work Session
        </button>
        <button 
          className={`tab-button ${activeTab === 'assignedTasks' ? 'active' : ''}`}
          onClick={() => handleTabChange('assignedTasks')}
        >
          Assigned Tasks
          {assignedTasks.length > 0 && (
            <span className="task-count">{assignedTasks.length}</span>
          )}
        </button>
        <button 
          className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => handleTabChange('history')}
        >
          Work History
        </button>
      </div>
      
      {activeTab === 'newSession' && (
        <>
          {/* Location detector at the top */}
          <div className="location-section" style={{ marginBottom: '20px' }}>
            <LocationDetector fields={propFields} onFieldDetected={handleFieldDetected} />
          </div>
          
          <div className="work-session-form" style={{ width: '100%' }}>
            <h3>{sessionActive ? 'Ongoing Work Session' : 'Start New Work Session'}</h3>
            
            <div className="form-group">
              <label htmlFor="fieldId">Field {propFields.length > 0 ? `(${propFields.length} available)` : '(No fields available)'}</label>
              <div className="field-selection-container">
                <select 
                  id="fieldId" 
                  name="fieldId" 
                  value={workSession.fieldId} 
                  onChange={handleInputChange}
                  disabled={sessionActive}
                  className={`${detectedField && detectedField.id === workSession.fieldId ? 'detected-field' : ''} ${formErrors.fieldId ? 'error-border' : ''}`}
                  style={{ width: '100%', padding: '10px', borderRadius: '4px', border: `1px solid ${formErrors.fieldId ? '#e74c3c' : '#ddd'}`, marginBottom: '10px' }}
                >
                  <option value="">Select a field</option>
                  {propFields && propFields.map(field => (
                    <option key={field.id} value={field.id}>{field.name || `Field ${field.id}`}</option>
                  ))}
                </select>
                {formErrors.fieldId && (
                  <div className="error-message" style={{ color: '#e74c3c', fontSize: '13px', marginTop: '-5px', marginBottom: '10px' }}>
                    {formErrors.fieldId}
                  </div>
                )}
                {detectedField && (
                  <div className="detected-field-info">
                    Your current location is in: <strong>{detectedField.name || `Field ${detectedField.id}`}</strong>
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="activityType">Activity Type</label>
              <select 
                id="activityType" 
                name="activityType" 
                value={workSession.activityType} 
                onChange={handleInputChange}
                disabled={sessionActive}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: `1px solid ${formErrors.activityType ? '#e74c3c' : '#ddd'}` }}
              >
                <option value="">Select an activity</option>
                {activityTypes.map(activity => (
                  <option key={activity.id} value={activity.id}>{activity.name}</option>
                ))}
              </select>
              {formErrors.activityType && (
                <div className="error-message" style={{ color: '#e74c3c', fontSize: '13px', marginTop: '5px' }}>
                  {formErrors.activityType}
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label>Equipment Used</label>
              {formErrors.fuelLevels && (
                <div className="error-message" style={{ color: '#e74c3c', fontSize: '13px', marginBottom: '10px' }}>
                  {formErrors.fuelLevels}
                </div>
              )}
              <div className="equipment-selection" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                {availableEquipment.map(equipment => (
                  <div 
                    key={equipment.id} 
                    style={{
                      backgroundColor: selectedEquipment.includes(equipment.id) ? '#d4f7e3' : '#f5f5f5',
                      border: selectedEquipment.includes(equipment.id) ? '1px solid #27ae60' : '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: equipment.status && equipment.status !== 'operational' ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      marginBottom: '15px',
                      padding: '15px',
                      opacity: equipment.status && equipment.status !== 'operational' ? 0.6 : 1,
                      position: 'relative'
                    }}
                    onClick={() => !sessionActive && (!equipment.status || equipment.status === 'operational') && handleEquipmentToggle(equipment.id)}
                  >
                    <div style={{ marginBottom: '10px' }}>
                      <span style={{ fontWeight: '500', display: 'block', fontSize: '16px' }}>{equipment.name}</span>
                      <span style={{ color: '#7f8c8d', fontSize: '12px', marginTop: '5px', display: 'block' }}>{equipment.type}</span>
                      {equipment.status && equipment.status !== 'operational' && (
                        <span style={{ color: '#e74c3c', fontSize: '11px', marginTop: '5px', fontWeight: '500' }}>{equipment.status}</span>
                      )}
                    </div>
                    
                    {selectedEquipment.includes(equipment.id) && ['tractor', 'harvester', 'sprayer'].includes(equipment.type.toLowerCase()) && (
                      <div style={{ 
                        padding: '12px', 
                        width: '100%', 
                        backgroundColor: '#e1f5fe', 
                        borderRadius: '4px', 
                        border: '1px solid #81d4fa',
                        marginTop: '15px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                          <label style={{ width: '170px', fontSize: '0.95rem', color: '#2c3e50', fontWeight: '500' }}>Starting Fuel Level (%):</label>
                          <input 
                            type="number" 
                            min="0" 
                            max="100"
                            value={(equipmentFuelLevels[equipment.id] && equipmentFuelLevels[equipment.id].start) || ''}
                            onChange={(e) => handleFuelLevelChange(equipment.id, 'start', e.target.value)}
                            disabled={sessionActive}
                            style={{ 
                              width: '80px', 
                              padding: '10px', 
                              border: '1px solid #90caf9', 
                              borderRadius: '4px',
                              marginRight: '8px'
                            }}
                            onClick={handleFuelInputClick}
                          />
                        </div>
                        {formErrors[`fuelLevel_${equipment.id}_start`] && (
                          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '5px', marginBottom: '10px' }}>
                            {formErrors[`fuelLevel_${equipment.id}_start`]}
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <label style={{ width: '170px', fontSize: '0.95rem', color: '#2c3e50', fontWeight: '500' }}>Ending Fuel Level (%):</label>
                          <input 
                            type="number" 
                            min="0" 
                            max="100"
                            value={(equipmentFuelLevels[equipment.id] && equipmentFuelLevels[equipment.id].end) || ''}
                            onChange={(e) => handleFuelLevelChange(equipment.id, 'end', e.target.value)}
                            disabled={!sessionActive}
                            style={{ 
                              width: '80px', 
                              padding: '10px', 
                              border: '1px solid #90caf9', 
                              borderRadius: '4px',
                              marginRight: '8px'
                            }}
                            onClick={handleFuelInputClick}
                          />
                        </div>
                        {formErrors[`fuelLevel_${equipment.id}_end`] && (
                          <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '10px' }}>
                            {formErrors[`fuelLevel_${equipment.id}_end`]}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {workSession.activityType && ['planting', 'fertilizing', 'spraying'].includes(workSession.activityType) && (
              <div className="form-group">
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '15px'
                }}>
                  <h4 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>Application Materials</h4>
                </div>
                
                {getRelevantMaterials().map(material => {
                  // Get applications for this material
                  const materialApps = materialApplications.filter(app => app.materialId === material.id);
                  const isSelected = selectedMaterials.includes(material.id);
                  
                  return (
                    <div key={material.id} style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '6px',
                      marginBottom: '15px',
                      overflow: 'hidden'
                    }}>
                      {/* Material header */}
                      <div style={{
                        padding: '12px 15px',
                        backgroundColor: isSelected ? '#f0f8ff' : '#f5f5f5',
                        borderBottom: isSelected ? '1px solid #e0e0e0' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: !sessionActive ? 'pointer' : 'default'
                      }} onClick={() => !sessionActive && handleMaterialToggle(material.id)}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '3px',
                          border: '1px solid ' + (isSelected ? '#3498db' : '#bbb'),
                          backgroundColor: isSelected ? '#3498db' : 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '11px',
                          marginRight: '8px'
                        }}>
                          {isSelected && <i className="fas fa-check"></i>}
                        </div>
                        <span style={{ fontWeight: '500', fontSize: '15px' }}>{material.name}</span>
                        <span style={{
                          backgroundColor: '#e1f5fe',
                          color: '#0288d1',
                          padding: '2px 8px',
                          borderRadius: '10px',
                          fontSize: '11px',
                          marginLeft: '8px'
                        }}>
                          {material.type}
                        </span>
                      </div>
                      
                      {/* Quantity input section */}
                      {isSelected && (
                        <div style={{ 
                          padding: '12px 15px',
                          backgroundColor: '#fafafa',
                          borderTop: 'none',
                          borderBottom: '1px solid #e0e0e0'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <label style={{ marginRight: '10px' }}>Quantity:</label>
                              <input 
                                type="number" 
                                min="0" 
                                step="0.1"
                                value={materialQuantities[material.id] || ''}
                                onChange={(e) => handleMaterialQuantityChange(material.id, e.target.value)}
                                style={{
                                  width: '80px',
                                  padding: '8px',
                                  border: '1px solid #ddd',
                                  borderRadius: '4px'
                                }}
                              />
                              <span style={{ marginLeft: '8px' }}>{material.unit}</span>
                            </div>
                            
                            {sessionActive ? (
                              <button 
                                onClick={() => handleAddApplication(material.id)}
                                style={{
                                  backgroundColor: '#4caf50',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '8px 15px',
                                  fontSize: '13px',
                                  cursor: 'pointer'
                                }}
                              >
                                Add Application
                              </button>
                            ) : (
                              <span style={{ color: '#777', fontSize: '13px', fontStyle: 'italic' }}>
                                Start session to add applications
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Material applications */}
                      {materialApps.length > 0 && (
                        <div style={{ padding: '12px 15px', borderTop: '1px solid #eee' }}>
                          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                            Applications ({materialApps.length})
                          </h4>
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {materialApps.map(app => (
                              <div key={app.id} style={{
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0',
                                borderRadius: '4px',
                                padding: '10px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <div>
                                    <span style={{ fontWeight: '500' }}>
                                      {app.quantity} {app.unit}
                                    </span>
                                    <span style={{ color: '#777', marginLeft: '8px', fontSize: '13px' }}>
                                      at {app.time}
                                    </span>
                                  </div>
                                  
                                  {sessionActive && (
                                    <button
                                      onClick={() => handleRemoveApplication(app.id)}
                                      style={{
                                        backgroundColor: '#f44336',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '12px',
                                        cursor: 'pointer'
                                      }}
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                                
                                {sessionActive ? (
                                  <textarea
                                    value={app.notes || ''}
                                    onChange={(e) => handleUpdateApplication(app.id, 'notes', e.target.value)}
                                    placeholder="Add notes (optional)"
                                    style={{
                                      width: '100%',
                                      padding: '8px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '13px',
                                      resize: 'vertical',
                                      minHeight: '40px',
                                      boxSizing: 'border-box'
                                    }}
                                  />
                                ) : app.notes ? (
                                  <div style={{ fontSize: '13px', color: '#555' }}>
                                    {app.notes}
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="form-group" style={{ marginBottom: '20px' }}>
              <label htmlFor="notes">Notes</label>
              <textarea 
                id="notes" 
                name="notes" 
                value={workSession.notes} 
                onChange={handleInputChange}
                placeholder="Add any additional notes here..."
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  resize: 'vertical',
                  minHeight: '80px'
                }}
              ></textarea>
            </div>
            
            <div style={{ 
              marginTop: '20px',
              padding: '15px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #eee',
              display: 'flex',
              justifyContent: !sessionActive ? 'flex-end' : 'space-between',
              alignItems: 'center'
            }}>
              {!sessionActive ? (
                <button 
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontWeight: '500',
                    cursor: !workSession.fieldId || !workSession.activityType ? 'not-allowed' : 'pointer',
                    opacity: !workSession.fieldId || !workSession.activityType ? 0.7 : 1
                  }}
                  onClick={handleStartSession}
                  disabled={!workSession.fieldId || !workSession.activityType}
                >
                  Start Work Session
                </button>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: '12px', color: '#777' }}>Session started:</span>
                    <span style={{ fontWeight: '500', fontSize: '16px' }}>
                      {workSession.startTime ? workSession.startTime.toLocaleTimeString() : 'Just now'}
                    </span>
                  </div>
                  <div>
                    <button 
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                      onClick={handleEndSession}
                    >
                      End & Save Session
                    </button>
                    <div style={{ fontSize: '11px', color: '#777', marginTop: '5px', textAlign: 'center' }}>
                      Complete the session to update inventory
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
      
      {activeTab === 'assignedTasks' && (
        <div className="assigned-tasks-section">
          <h3>Your Assigned Tasks</h3>
          
          {assignedTasks.length > 0 ? (
            <div className="task-cards">
              {assignedTasks.map(task => (
                <div key={task.id} className={`task-card priority-${task.priority}`}>
                  <div className="task-header">
                    <h4>{getFieldName(task.fieldId)}: {taskTypes.find(t => t.id === task.taskType)?.name}</h4>
                    <span className={`priority-badge ${task.priority}`}>
                      {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                    </span>
                  </div>
                  
                  <div className="task-details">
                    <p className="task-description">{task.description}</p>
                    <div className="task-meta">
                      <span className="task-date">
                        <i className="fas fa-calendar"></i> {new Date(task.scheduledDate).toLocaleDateString()}
                      </span>
                      {task.materials && task.materials.length > 0 && (
                        <span className="task-materials">
                          <i className="fas fa-flask"></i> {task.materials.length} material(s)
                        </span>
                      )}
                      {task.equipment && task.equipment.length > 0 && (
                        <span className="task-equipment">
                          <i className="fas fa-tools"></i> {task.equipment.length} equipment
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="task-actions">
                    <button 
                      className="begin-task-btn"
                      onClick={() => handleStartAssignedTask(task)}
                      disabled={task.status === 'inProgress'}
                    >
                      {task.status === 'inProgress' ? 'In Progress' : 'Begin Task'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-tasks">
              <p>You have no assigned tasks at the moment.</p>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'history' && (
        <div className="work-history">
          <h3>Recent Work History</h3>
          <table className="sessions-table">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Field</th>
                <th>Activity</th>
                <th>Duration</th>
                <th>Equipment</th>
                <th>Material Totals</th>
              </tr>
            </thead>
            <tbody>
              {workSessions.slice(0, 10).map((session, index) => (
                <React.Fragment key={index}>
                  <tr 
                    onClick={() => setExpandedSessionId(expandedSessionId === session.id ? null : session.id)}
                    style={{ cursor: 'pointer', backgroundColor: expandedSessionId === session.id ? '#f0f7ff' : 'transparent' }}
                  >
                    <td>
                      <button style={{ 
                        background: 'none', 
                        border: 'none', 
                        cursor: 'pointer',
                        padding: '2px 5px',
                        fontSize: '16px'
                      }}>
                        {expandedSessionId === session.id ? '▼' : '►'}
                      </button>
                    </td>
                    <td>{session.startTime ? new Date(session.startTime).toLocaleDateString() : 'Unknown'}</td>
                    <td>{getFieldName(session.fieldId)}</td>
                    <td>{activityTypes.find(a => a.id === session.activityType)?.name || session.activityType}</td>
                    <td>{session.duration ? `${session.duration} min` : 'N/A'}</td>
                    <td>{session.equipment && session.equipment.map ? 
                      session.equipment.map(equipment => equipment.name).filter(Boolean).join(', ') 
                      : 'None'
                    }</td>
                    <td>
                      {session.materialTotals && session.materialTotals.length > 0 ? 
                        session.materialTotals.map(m => 
                          `${m.name}: ${m.quantity} ${m.unit}`
                        ).join(', ')
                        : 'None'
                      }
                    </td>
                  </tr>
                  {expandedSessionId === session.id && (
                    <tr>
                      <td colSpan="7" style={{ padding: '0' }}>
                        <div style={{ 
                          padding: '15px', 
                          backgroundColor: '#f8fafc', 
                          border: '1px solid #e1e8ed',
                          borderRadius: '0 0 4px 4px'
                        }}>
                          {/* Detailed view with all applications */}
                          <h4 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Application Details</h4>
                          
                          {session.materials && session.materials.length > 0 ? (
                            <div style={{ marginBottom: '15px' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#edf2f7' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Material</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Quantity</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Time</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {session.materials.map((material, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #edf2f7' }}>
                                      <td style={{ padding: '8px' }}>{material.name}</td>
                                      <td style={{ padding: '8px' }}>{material.type}</td>
                                      <td style={{ padding: '8px' }}>{material.quantity} {material.unit}</td>
                                      <td style={{ padding: '8px' }}>{material.time}</td>
                                      <td style={{ padding: '8px' }}>{material.notes || 'No notes'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p>No materials were used in this session.</p>
                          )}
                          
                          {/* Material Totals */}
                          {session.materialTotals && session.materialTotals.length > 0 && (
                            <div>
                              <h4 style={{ margin: '15px 0 10px 0', fontSize: '16px' }}>Material Totals</h4>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#edf2f7' }}>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Material</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Type</th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Total Quantity</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {session.materialTotals.map((material, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #edf2f7' }}>
                                      <td style={{ padding: '8px' }}>{material.name}</td>
                                      <td style={{ padding: '8px' }}>{material.type}</td>
                                      <td style={{ padding: '8px', fontWeight: 'bold' }}>{material.quantity} {material.unit}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {workSessions.length === 0 && (
                <tr>
                  <td colSpan="7" className="empty-message">No work sessions recorded</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkerInterface; 